import { useEffect, useState } from "react";
import { Switch, FormControlLabel } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import FormField from "components/FormField";
import { useAgencyInfo } from "context/agency";
import { useAppServices } from "hook/services";
import toast from "react-hot-toast";
import PrimaryButton from "../../../components/PrimaryButton";

function RedirectSettings() {
  const [agency, Update] = useAgencyInfo();
  const AppService = useAppServices();
  const [enableClaimListingCustomRedirect, setEnableClaimListingCustomRedirect] = useState(
    agency?.enableClaimListingCustomRedirect || false
  );
  const [defaultBillingPeriod, setDefaultBillingPeriod] = useState(
    agency?.default_billing_period === "yearly" ? "yearly" : "monthly"
  );
  const [redirectProcessing, setRedirectProcessing] = useState(false);

  useEffect(() => {
    setEnableClaimListingCustomRedirect(agency?.enableClaimListingCustomRedirect || false);
    setDefaultBillingPeriod(
      agency?.default_billing_period === "yearly" ? "yearly" : "monthly"
    );
  }, [agency?.enableClaimListingCustomRedirect, agency?.default_billing_period]);

  const handleSaveRedirectSettings = async (e) => {
    e.preventDefault();
    setRedirectProcessing(true);

    const formData = new FormData(e.target);
    const claimListingRedirectUrl = (formData.get("claimListingRedirectUrl") || "").trim();
    const upgradePageHeaderText = (formData.get("upgradePageHeaderText") || "").trim();

    const payload = {
      _id: agency._id,
      enableClaimListingCustomRedirect,
      claimListingRedirectUrl: claimListingRedirectUrl || null,
      upgradePageHeaderText: upgradePageHeaderText || null,
      default_billing_period: defaultBillingPeriod,
    };

    const { response } = await AppService.agency.update({ payload });

    if (response?.success) {
      Update(response.data);
      toast.success("Redirect settings saved!");
    } else {
      toast.error("Failed to save redirect settings");
    }

    setRedirectProcessing(false);
  };

  return (
    <SoftBox
      mt={2}
      width="98%"
      display="flex"
      flexDirection="column"
      gap={2}
      component="form"
      onSubmit={handleSaveRedirectSettings}
    >
      <div style={{ width: "50%", marginTop: "10px" }}>
        <FormField
          label="Upgrade Page Header Text"
          name="upgradePageHeaderText"
          defaultValue={agency?.upgradePageHeaderText}
          width="100%"
          placeholder="Thank You For Claiming Your Listing"
        />
        <p style={{ fontSize: "12px", color: "#666", marginTop: "10px", marginBottom: "0" }}>
          Shown as the heading on the upgrade page. Leave blank to keep the default heading.
        </p>
      </div>

      <div style={{ width: "50%" }}>
        <SoftTypography
          variant="caption"
          fontWeight="bold"
          color="text"
          sx={{ display: "block", mb: 0.5 }}
        >
          Default Billing Period on Pricing Page
        </SoftTypography>
        <select
          name="default_billing_period"
          value={defaultBillingPeriod}
          onChange={(e) => setDefaultBillingPeriod(e.target.value)}
          style={{
            width: "100%",
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid #d2d6da",
            fontSize: 14,
            background: "#fff",
          }}
        >
          <option value="monthly">Show Monthly first</option>
          <option value="yearly">Show Yearly (Annually) first</option>
        </select>
        <p style={{ fontSize: "12px", color: "#666", marginTop: "10px", marginBottom: "0" }}>
          Controls which billing cycle is selected by default when both monthly and yearly prices
          exist. Users can still switch between them.
        </p>
      </div>

      <SoftTypography variant="h6" fontWeight="bold">
        Redirect Settings
      </SoftTypography>

      <FormControlLabel
        control={
          <Switch
            checked={enableClaimListingCustomRedirect}
            onChange={async (e) => {
              const newValue = e.target.checked;
              setEnableClaimListingCustomRedirect(newValue);

              const payload = {
                _id: agency._id,
                enableClaimListingCustomRedirect: newValue,
              };

              try {
                const { response } = await AppService.agency.update({
                  payload,
                });

                if (response?.success) {
                  Update(response.data);
                  toast.success("Claim listing redirect setting updated!");
                }
              } catch (error) {
                console.error("Failed to update claim listing redirect setting:", error);
                setEnableClaimListingCustomRedirect(!newValue);
                toast.error("Failed to update claim listing redirect setting");
              }
            }}
          />
        }
        label="Use Custom Redirect After Claiming a Listing"
        sx={{ marginLeft: 0 }}
      />

      {enableClaimListingCustomRedirect && (
        <div style={{ width: "50%" }}>
          <FormField
            label="Claim Listing Redirect URL"
            name="claimListingRedirectUrl"
            defaultValue={agency?.claimListingRedirectUrl}
            width="100%"
            placeholder="upgrade-page"
          />
        </div>
      )}

      <SoftBox>
        <PrimaryButton type="submit" disabled={redirectProcessing} style={{ marginTop: 16 }}>
          {redirectProcessing ? "Saving..." : "Save Redirect Settings"}
        </PrimaryButton>
      </SoftBox>
    </SoftBox>
  );
}

export default RedirectSettings;
